import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import Modal from "../../layouts/Modal";
import { ContactType } from "../../models/Contact";
import useWilmaUsers from "../../data/useWilmaUsers";
import { TextInput } from "../shared/Inputs/TextInput";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { IWilmaUser } from "./../../models/auth/WilmaUser";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useAuth } from "../../contexts/AuthContext";
import StaffPasswordModal from "./StaffPasswordModal";
import useCustomerDetails from "../../data/useCustomerDetails";

type FormValues = {
  forename: string;
  surname: string;
  email: string;
  username: string;
  dismantleTarget: string;
  uname: string;
};

interface StaffModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  staff: IWilmaUser | undefined;
}

const claimsToType = (user: IWilmaUser) => {
  var mappedClaims : Record<string, string> = {};

  user.userClaims.forEach((claim) => mappedClaims[claim.type] = claim.value);

  if(mappedClaims.isAdmin    === "true") return ContactType.Admin;
  if(mappedClaims.isDriver   === "true") return ContactType.Driver;
  if(mappedClaims.isStaff    === "true") return ContactType.Staff;
  if(mappedClaims.isMechanic === "true") return ContactType.Mechanic;
  if(mappedClaims.isExporter === "true") return ContactType.Exporter;
};

const typeToClaims = (type: ContactType) => {
  const template = {
    isAdmin: false,
    isDriver: false,
    isStaff: false,
    isMechanic: false,
    isExporter: false,
  };

  switch (type) {
    case ContactType.Admin:
      return { ...template, isAdmin: true };
    case ContactType.Driver:
      return { ...template, isDriver: true };
    case ContactType.Staff:
      return { ...template, isStaff: true };
    case ContactType.Mechanic:
      return { ...template, isMechanic: true };
    case ContactType.Exporter:
      return { ...template, isExporter: true };
    default:
      return template;
  }
};

export default function StaffModal({ open, setOpen, staff }: StaffModalProps) {
  const { customerId } = useAuth();
  const { saveHandlers } = useDefaultCRUDHandlers("Staff");
  const { update, create, remove } = useWilmaUsers();
  const { customer } = useCustomerDetails();
  const [prefix, setPrefix] = useState<string>("");
  const [contactType, setContactType] = useState<ContactType>(
    ContactType.Staff
  );
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [updatePasswordOpen, setUpdatePasswordOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset, watch, setValue } = useForm<FormValues>();

  const contactTypes: Record<number, string> = {
    4: "Admin",
    2: "Staff",
    3: "Driver",
    5: "Mechanic",
    6: "Exporter"
  };

  const onSubmit = (data: any) => {
    const claimsObj: Record<string, boolean> = typeToClaims(contactType)!;

    const claims = Object.keys(claimsObj).reduce<Record<string, string>>(
      (acc, key) => {
        acc[key] = claimsObj[key] ? "true" : "false";
        return acc;
      },
      {}
    );

    const updatedStaff: any = {
      ...staff,
      ...data,
      dismantleTarget: data.dismantleTarget !== "" ? parseInt(data.dismantleTarget) : null,
      claims: claims,
      customerId,
      status: data.email !== "" ? 3 : 2, //Invited
      email: data.email !== "" ? data.email : null,
    };

    if (staff !== undefined) {
      update.mutate(updatedStaff, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError,
      });
    } else {
      create.mutate(updatedStaff, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError,
      });
    }
  };

  const handleDelete = () => {
    remove.mutate(staff?.id!, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: saveHandlers.onError,
    });
  };

  useEffect(() => {
    if (!customer.isLoading && customer.data) {
      setPrefix(customer.data.data.usernamePrefix || "");
    }
  }, [customer.isLoading, customer.data]);

  useEffect(() => {
    if (staff !== undefined) {
      setContactType(claimsToType(staff)!);
      reset({
        ...staff,
        dismantleTarget: staff.dismantleTarget ? String(staff.dismantleTarget) : "",
        uname: staff.username.replace(`${prefix}-`, ""),
      });
    } else {
      reset({
        forename: "",
        surname: "",
        email: "",
        username: "",
        dismantleTarget: "",
      });
    }
  }, [staff, open, reset]);

  const uname = watch("uname");

  useEffect(() => {
    if (uname) {
      const formattedUname = uname.toLowerCase().replace(/[^a-z0-9]/g, "");
      // check if its an email
      if (formattedUname.includes("@")) {
        setValue("username", formattedUname);
        return;
      }

      setValue("username", `${prefix}-${formattedUname}`);
    } else setValue("username", "");
  }, [uname]);

  return (
    <>
      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        title={"Delete staff"}
        message={`Are you sure you want to delete this staff member?\n This action cannot be undone.`}
        confirmButtonText={"Delete"}
        onConfirm={handleDelete}
        isLoading={false}
      />
      <StaffPasswordModal open={updatePasswordOpen} setOpen={setUpdatePasswordOpen} staff={staff} />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen}>
        <h1 className="text-xl">{`${staff ? "Edit" : "Add"} Staff`}</h1>
        <div className="mt-4">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4 border-t border-gray-200"
          >
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-2">
              <TextInput
                label="First Name"
                registerName="forename"
                register={register}
              />
              <TextInput
                label="Last Name"
                registerName="surname"
                register={register}
              />

              <div className="flex items-end gap-2">
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-[3px]">
                    Prefix
                  </label>
                  <div className="p-[6px] px-3 mt-[5px] bg-gray-300 text-gray-500 rounded border-b border-gray-400 shadow">
                    {prefix}
                  </div>
                </div>
                <span className="text-sm font-medium pb-2">
                  -
                </span>
                <TextInput
                  label="Username"
                  registerName="uname"
                  register={register}
                  // customClasses="pl-10"
                />
              </div>

              <TextInput
                label="Your Username"
                disabled={true}
                registerName="username"
                register={register}
              />
              

              <TextInput
                label="Email"
                disabled={staff !== undefined}
                registerName="email"
                register={register}
              />

              <div className="sm:col-span-1">
                <label className="text-sm font-medium text-gray-500">
                  Role
                </label>
                <select
                  aria-label="Contact type filter"
                  value={contactType}
                  onChange={(e) => setContactType(parseInt(e.target.value))}
                  className="w-full mt-1 text-sm text-gray-900 h-[40px]"
                >
                  {Object.keys(contactTypes).map((key, i) => (
                    <option className="sm:text-sm" key={i} value={key}>
                      {contactTypes[Number(key)]}
                    </option>
                  ))}
                </select>
              </div>

              <TextInput
                label="Password"
                disabled={staff !== undefined}
                registerName="password"
                register={register}
                type="password"
              />
              <TextInput
                label="Dismantle Target"
                registerName="dismantleTarget"
                register={register}
              />
              <div /> {/* spacer */}
              <div /> {/* spacer */}
              <ButtonInput
                label={"Save"}
                isSubmit={true}
                onClick={undefined}
                classes={undefined}
              />
              {staff !== undefined && (
                <ButtonInput
                  label={"Delete"}
                  isSubmit={false}
                  onClick={() => setDeleteOpen(true)}
                  classes={"!bg-red-500 hover:!bg-red-600"}
                />
              )}
              {staff !== undefined && (
                <div className="mt-2 text-sm text-gray-500 sm:col-span-2 text-center hover:text-gray-700 cursor-pointer" onClick={() => setUpdatePasswordOpen(true)} >
                  Change Password
                </div>
              )}
              
            </div>
          </form>
        </div >
      </Modal >
    </>
  );
}
